import React from 'react';
import {
  CheckIcon,
  XMarkIcon,
  DocumentTextIcon,
} from '../../icons';
import MatDashPanel from '../layout/MatDashPanel';
import ToastEditor from './ToastEditor';
import ToastViewer from './ToastViewer';
import CourtSummaryDocument from './CourtSummaryDocument';
import { formatSummary, isUsableSummary } from './transcriptionUtils';
import { buildCourtSummaryDocument } from './courtSummaryDoc';

const SummarySection = ({
  transcription,
  summary,
  isEditingSummary,
  editedSummary,
  setEditedSummary,
  handleSaveSummary,
  handleCancelSummaryEdit,
  loading,
  isProcessing = false,
}) => {
  if (!transcription) return null;

  const usableSummary = isUsableSummary(summary);
  const showLoading = isProcessing && !usableSummary && !isEditingSummary;
  // Styled as the 8-part Judicial Service of Ghana transcript template; falls back
  // to the plain markdown viewer when there is nothing to template.
  const courtDocument = usableSummary
    ? buildCourtSummaryDocument(summary, transcription, false, null)
    : null;

  const statusBadge = showLoading ? (
    <span className="inline-flex items-center px-3 py-1 bg-matdash-warning-subtle text-amber-900 text-xs font-medium rounded-full border border-matdash-warning/30">
      Processing
    </span>
  ) : usableSummary ? (
    <span className="inline-flex items-center px-3 py-1 bg-matdash-success-subtle text-emerald-800 text-xs font-medium rounded-full border border-emerald-200">
      Available
    </span>
  ) : (
    <span className="inline-flex items-center px-3 py-1 bg-matdash-warning-subtle text-amber-900 text-xs font-medium rounded-full border border-matdash-warning/30">
      Pending
    </span>
  );

  return (
    <div id="summary-section" className="mt-4 sm:mt-6">
      <MatDashPanel
        title="Summary"
        subtitle="Generated by Snwolley AI"
        icon={DocumentTextIcon}
        variant="primary"
        headerAction={statusBadge}
        className="mb-0"
      >
        {showLoading ? (
          <div className="md-empty-state py-8 sm:py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-matdash-primary-subtle rounded-full mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-matdash-primary" />
            </div>
            <h3 className="text-lg font-semibold text-matdash-dark mb-2">Summary in Progress</h3>
            <p className="text-matdash-muted text-sm max-w-md mx-auto">
              The regenerated transcript is still processing. The summary will appear here when it is ready.
            </p>
          </div>
        ) : usableSummary || isEditingSummary ? (
          <div className="space-y-4">
            {isEditingSummary ? (
              <div>
                <div className="toast-editor-wrapper">
                  <ToastEditor
                    content={editedSummary}
                    onChange={(content) => setEditedSummary(content)}
                    placeholder="Edit summary content..."
                    height="500px"
                    initialEditType="wysiwyg"
                    previewStyle="vertical"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <button
                    onClick={handleSaveSummary}
                    disabled={loading}
                    className="flex-1 md-btn md-btn-success justify-center"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : (
                      <CheckIcon className="h-4 w-4 mr-2" />
                    )}
                    {loading ? 'Saving...' : 'Save Summary'}
                  </button>
                  <button
                    onClick={handleCancelSummaryEdit}
                    disabled={loading}
                    className="flex-1 md-btn md-btn-danger justify-center"
                  >
                    <XMarkIcon className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : courtDocument ? (
              <CourtSummaryDocument model={courtDocument} />
            ) : (
              <div className="md-viewer-well">
                <ToastViewer
                  content={formatSummary(summary)}
                  className="toast-viewer-content"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="md-empty-state py-8 sm:py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-matdash-primary-subtle rounded-full mb-4">
              <DocumentTextIcon className="h-8 w-8 text-matdash-primary" />
            </div>
            <h3 className="text-lg font-semibold text-matdash-dark mb-2">No summary yet</h3>
            <p className="text-matdash-muted text-sm max-w-md mx-auto">
              A summary will appear here once generation is complete.
            </p>
          </div>
        )}
      </MatDashPanel>
    </div>
  );
};

export default SummarySection;
